import { useCallback, useEffect, useState } from "react";

// AirPlay support is WebKit-only (Safari on macOS/iOS). It reaches Apple TVs and any
// AirPlay-2-capable receiver on the network — which includes LG webOS TVs from 2019 on.
// HLS plays over AirPlay natively because Safari hands the same .m3u8 URL to the receiver.
//
// We feature-detect via `WebKitPlaybackTargetAvailabilityEvent`, mark the <video> element as
// AirPlay-eligible, and only expose the button once the OS reports a receiver is available so
// it stays hidden in Chrome and on networks with nothing to cast to.

interface AirPlayVideo extends HTMLVideoElement {
  webkitShowPlaybackTargetPicker?: () => void;
  webkitCurrentPlaybackTargetIsWireless?: boolean;
}

export function useAirPlay(video: HTMLVideoElement | null) {
  const [available, setAvailable] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!video) return;
    // Only Safari exposes this constructor; bail (button stays hidden) everywhere else.
    if (typeof (window as unknown as { WebKitPlaybackTargetAvailabilityEvent?: unknown }).WebKitPlaybackTargetAvailabilityEvent === "undefined") {
      return;
    }

    const el = video as AirPlayVideo;
    el.setAttribute("x-webkit-airplay", "allow");

    const onAvailability = (e: Event) => {
      setAvailable((e as Event & { availability?: string }).availability === "available");
    };
    const onWirelessChange = () => setActive(!!el.webkitCurrentPlaybackTargetIsWireless);

    el.addEventListener("webkitplaybacktargetavailabilitychanged", onAvailability);
    el.addEventListener("webkitcurrentplaybacktargetiswirelesschanged", onWirelessChange);
    // Reflect any target already engaged when the player mounts.
    onWirelessChange();

    return () => {
      el.removeEventListener("webkitplaybacktargetavailabilitychanged", onAvailability);
      el.removeEventListener("webkitcurrentplaybacktargetiswirelesschanged", onWirelessChange);
    };
  }, [video]);

  const showPicker = useCallback(() => {
    (video as AirPlayVideo | null)?.webkitShowPlaybackTargetPicker?.();
  }, [video]);

  return { available, active, showPicker };
}
